import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SchoolCreate from '../../create/js/schoolCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { useApp } from '../../../../context/AppContext';
import { schoolAPI } from '../../../../utils/api';
import PropTypes from 'prop-types';

function SchoolTable({ onCreateSchool, reloadKey = 0 }) {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState('All');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));
  const canCreate = permissions.includes('CREATE_SCHOOL') || isSuperAdmin;
  const canEdit = permissions.includes('EDIT_SCHOOL') || isSuperAdmin;
  const canDelete = permissions.includes('DELETE_SCHOOL') || isSuperAdmin;

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await schoolAPI.getAll();
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to fetch schools:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools, reloadKey]);

  const handleView = school => {
    setSelectedSchool(school);
    setShowViewModal(true);
  };

  const handleEdit = school => {
    setSelectedSchool(school);
    setShowEditModal(true);
  };

  const handleDelete = async (school) => {
    const result = await Swal.fire({
      title: 'Delete School?',
      text: `Are you sure you want to delete ${school.school_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await schoolAPI.delete(school.id);
        await fetchSchools();
        Swal.fire('Deleted!', 'School has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.message || 'Failed to delete school', 'error');
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'semis_code', header: 'SEMIS', size: 100 },
    { accessorKey: 'school_name', header: 'School Name', size: 250 },
    { accessorKey: 'school_type', header: 'Type', size: 100 },
    { accessorKey: 'level', header: 'Level', size: 100 },
    { accessorKey: 'taluka_name', header: 'Taluka', size: 150 },
    { accessorKey: 'to_teo_name', header: 'TO/TEO', size: 150 },
    { accessorKey: 'head_master_user_name', header: 'Head Master User', size: 170 },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => handleView(row.original)}><FaEye /></button>
          {canEdit && <button className="eduss-global-action-btn edit" onClick={() => handleEdit(row.original)}><FaEdit /></button>}
          {canDelete && <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(s => {
    const matchesSearch = s.school_name?.toLowerCase().includes(filter.toLowerCase()) || 
                         s.semis_code?.toLowerCase().includes(filter.toLowerCase());
    const matchesType = typeFilter === 'All' || s.school_type === typeFilter;
    const matchesLevel = levelFilter === 'All' || s.level === levelFilter;
    return matchesSearch && matchesType && matchesLevel;
  });

  return (
    <div className="premium-card">
      <TableToolbar 
        title="Schools"
        subtitle="Manage all institutional records and their information"
        onSearch={setFilter}
        onAdd={canCreate ? onCreateSchool : null}
        addText="Add School"
        showImportExport={true}
        filters={[
          {
            label: 'Type',
            value: typeFilter,
            onChange: setTypeFilter,
            options: [
              { label: 'All Types', value: 'All' },
              { label: 'Boys', value: 'Boys' },
              { label: 'Girls', value: 'Girls' },
              { label: 'Mixed', value: 'Mixed' }
            ]
          },
          {
            label: 'Level',
            value: levelFilter,
            onChange: setLevelFilter,
            options: [
              { label: 'All Levels', value: 'All' },
              { label: 'Primary', value: 'Primary' },
              { label: 'Middle', value: 'Middle' },
              { label: 'High', value: 'High' },
              { label: 'Higher Secondary', value: 'Higher Secondary' }
            ]
          }
        ]}
        onExport={() => schoolAPI.exportCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'schools.csv');
          document.body.appendChild(link);
          link.click();
        })}
        onImport={async (file) => {
          const text = await file.text();
          await schoolAPI.importCsv(text);
          fetchSchools();
          Swal.fire('Success', 'Schools imported successfully', 'success');
        }}
        onDownloadSample={() => schoolAPI.sampleCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'schools_sample.csv');
          document.body.appendChild(link);
          link.click();
        })}
      />

      <div style={{ padding: '0 30px 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
        />
      </div>

      {showViewModal && selectedSchool && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>School Details</h3>
              <button onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>SEMIS Code</th><td>{selectedSchool.semis_code}</td></tr>
                  <tr><th>School Name</th><td>{selectedSchool.school_name}</td></tr>
                  <tr><th>Type</th><td>{selectedSchool.school_type}</td></tr>
                  <tr><th>Level</th><td>{selectedSchool.level}</td></tr>
                  <tr><th>Taluka</th><td>{selectedSchool.taluka_name}</td></tr>
                  <tr><th>Headmaster</th><td>{selectedSchool.headmaster_name}</td></tr>
                  <tr><th>Assigned TO/TEO</th><td>{selectedSchool.to_teo_name || '-'}</td></tr>
                  <tr><th>Head Master User</th><td>{selectedSchool.head_master_user_name || '-'}</td></tr>
                  <tr><th>Phone</th><td>{selectedSchool.phone || '-'}</td></tr>
                  <tr><th>Address</th><td>{selectedSchool.address || '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showEditModal && selectedSchool && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Edit School</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <SchoolCreate
                school={selectedSchool}
                onClose={() => setShowEditModal(false)}
                onSubmit={() => { fetchSchools(); setShowEditModal(false); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

SchoolTable.propTypes = {
  onCreateSchool: PropTypes.func,
  reloadKey: PropTypes.number
};

export default SchoolTable;
